import React, { useMemo, useState } from "react";
import OrbitalLoader from "../../common/OrbitalLoader";

const METRIC_OPTIONS = [
  { value: "count", label: "Count" },
  { value: "count_views", label: "Views" },
  { value: "count_likes", label: "Likes" },
  { value: "count_comments", label: "Comments" },
];

const formatDayLabel = (ts) => {
  const d = new Date(ts);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const formatMonthLabel = (ts) => {
  const d = new Date(ts);
  return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
};

const monthStart = (ts) => {
  const d = new Date(ts);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

const addMonthsMs = (ts, delta) => {
  const d = new Date(ts);
  d.setMonth(d.getMonth() + delta);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

const formatYValue = (v) => {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return String(Math.round(v));
};

const median = (arr) => {
  if (!arr?.length) return null;
  const s = [...arr].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

const bucketKey = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

const oneDayMs = 24 * 60 * 60 * 1000;

const TimelineChart = ({ videoRows, loading = false }) => {
  const [metric, setMetric] = useState("count");
  const [granularity, setGranularity] = useState("daily"); // "daily" | "monthly"
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const { bucketKeys, bucketData, firstDate, lastDate, formatBucketLabel } = useMemo(() => {
    const isCount = metric === "count";
    const now = Date.now();
    const withDate = (videoRows || []).filter((r) => r.video?.uploaded_at);

    let keys;
    if (granularity === "daily") {
      const endDay = bucketKey(now);
      const startDay = endDay - 29 * oneDayMs;
      keys = [];
      for (let t = startDay; t <= endDay; t += oneDayMs) keys.push(t);
    } else {
      const endMonth = monthStart(now);
      const startMonth = addMonthsMs(endMonth, -11);
      keys = [];
      for (let t = startMonth; t <= endMonth; t = addMonthsMs(t, 1)) keys.push(t);
    }

    const bucketKeyForUpload = (uploadedAt) =>
      granularity === "daily" ? bucketKey(uploadedAt) : monthStart(uploadedAt);

    const buckets = {};
    withDate.forEach((r) => {
      const k = bucketKeyForUpload(r.video.uploaded_at);
      if (!keys.includes(k)) return;
      if (!buckets[k]) buckets[k] = { count: 0, values: [] };
      buckets[k].count += 1;
      const val = r.video?.[metric];
      if (!isCount && val != null) buckets[k].values.push(Number(val));
    });

    const data = {};
    keys.forEach((key) => {
      const b = buckets[key];
      if (!b) {
        data[key] = { count: 0, displayValue: 0 };
        return;
      }
      if (isCount) {
        data[key] = { count: b.count, displayValue: b.count };
      } else {
        const med = median(b.values) ?? 0;
        data[key] = {
          count: b.count,
          displayValue: med,
          min: b.values.length ? Math.min(...b.values) : 0,
          max: b.values.length ? Math.max(...b.values) : 0,
        };
      }
    });

    return {
      bucketKeys: keys,
      bucketData: data,
      firstDate: keys[0] ?? null,
      lastDate: keys[keys.length - 1] ?? null,
      formatBucketLabel: granularity === "daily" ? formatDayLabel : formatMonthLabel,
    };
  }, [videoRows, metric, granularity]);

  const isCount = metric === "count";

  if (loading && !(videoRows && videoRows.length)) {
    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-theme-text opacity-70 uppercase tracking-wider">Timeline</span>
        </div>
        <div className="flex items-center justify-center h-20">
          <OrbitalLoader size={36} />
        </div>
      </div>
    );
  }

  if (bucketKeys.length === 0) {
    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-theme-text opacity-70 uppercase tracking-wider">Timeline</span>
        </div>
        <div className="flex items-center justify-center h-20 text-theme-text opacity-50 text-sm">
          Timeline unavailable.
        </div>
      </div>
    );
  }

  const maxValue = Math.max(1, ...bucketKeys.map((k) => bucketData[k]?.displayValue ?? 0));
  const hoveredKey = hoveredIndex != null ? bucketKeys[hoveredIndex] : null;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <span className="text-xs text-theme-text opacity-70 uppercase tracking-wider">Timeline</span>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded border border-theme-secondary overflow-hidden text-xs">
            <button
              type="button"
              onClick={() => setGranularity("daily")}
              className={`px-2 py-1 ${granularity === "daily" ? "bg-theme-text text-theme-main" : "bg-theme-main text-theme-text opacity-80 hover:opacity-100"}`}
            >
              30 days
            </button>
            <button
              type="button"
              onClick={() => setGranularity("monthly")}
              className={`px-2 py-1 border-l border-theme-secondary ${granularity === "monthly" ? "bg-theme-text text-theme-main" : "bg-theme-main text-theme-text opacity-80 hover:opacity-100"}`}
            >
              12 months
            </button>
          </div>
          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            className="text-xs px-2 py-1 rounded border border-theme-secondary bg-theme-main text-theme-text"
          >
            {METRIC_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="relative h-16">
        {hoveredKey != null && bucketData[hoveredKey] && (
          <div
            className="absolute z-10 px-2 py-1.5 rounded bg-theme-secondary border border-theme-secondary text-theme-text text-xs shadow-lg pointer-events-none whitespace-nowrap"
            style={{
              left: `${((hoveredIndex + 0.5) / bucketKeys.length) * 100}%`,
              transform: "translate(-50%, -100%)",
              top: 0,
              marginTop: "-4px",
            }}
          >
            <div className="font-medium">{formatBucketLabel(hoveredKey)}</div>
            <div className="text-theme-text opacity-80 mt-0.5">
              {isCount
                ? `${bucketData[hoveredKey].count} video${bucketData[hoveredKey].count === 1 ? "" : "s"}`
                : `median ${formatYValue(bucketData[hoveredKey].displayValue)} · ${bucketData[hoveredKey].count} video${bucketData[hoveredKey].count === 1 ? "" : "s"}`
              }
            </div>
          </div>
        )}
        <div className="absolute inset-0 flex items-end gap-px">
          {bucketKeys.map((key, i) => {
            const d = bucketData[key];
            const val = d?.displayValue ?? 0;
            const heightPct = maxValue > 0 ? (val / maxValue) * 100 : 0;
            const isEmpty = val === 0;
            const isHovered = hoveredIndex === i;
            return (
              <div
                key={key}
                className={`flex-1 min-w-0 rounded-t-sm transition-opacity cursor-pointer bg-theme-text ${isHovered ? "opacity-60" : isEmpty ? "opacity-[0.06] hover:opacity-15" : "opacity-25 hover:opacity-35"}`}
                style={{ height: `${Math.max(heightPct, 4)}%` }}
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
              />
            );
          })}
        </div>
      </div>
      <div className="flex justify-between text-[10px] text-theme-text opacity-40 mt-1">
        <span>{firstDate != null ? formatBucketLabel(firstDate) : ""}</span>
        <span>{lastDate != null ? formatBucketLabel(lastDate) : ""}</span>
      </div>
    </div>
  );
};

export default TimelineChart;
